'use client';

import React, { useState } from 'react';
import Link from 'next/link';

const plans = [
  {
    id: 'plan-free',
    name: 'Free',
    price: { monthly: 0, annual: 0 },
    description: 'Perfect for getting started',
    cta: 'Start Free',
    ctaStyle: 'border border-border text-foreground hover:bg-muted',
    popular: false,
    features: [
      { id: 'f-free-1', text: '1 Bio Page', included: true },
      { id: 'f-free-2', text: 'Up to 5 links', included: true },
      { id: 'f-free-3', text: '3 basic templates', included: true },
      { id: 'f-free-4', text: 'Basic analytics (7 days)', included: true },
      { id: 'f-free-5', text: 'BioLink branding', included: false },
      { id: 'f-free-6', text: 'Custom themes', included: false },
      { id: 'f-free-7', text: 'Priority support', included: false },
    ],
  },
  {
    id: 'plan-pro',
    name: 'Pro',
    price: { monthly: 399, annual: 299 },
    description: 'For serious creators',
    cta: 'Start Pro Trial',
    ctaStyle: 'btn-primary',
    popular: true,
    features: [
      { id: 'f-pro-1', text: '1 Bio Page', included: true },
      { id: 'f-pro-2', text: 'Unlimited links', included: true },
      { id: 'f-pro-3', text: 'All 10+ templates', included: true },
      { id: 'f-pro-4', text: 'Analytics (90 days)', included: true },
      { id: 'f-pro-5', text: 'Remove BioLink branding', included: true },
      { id: 'f-pro-6', text: 'Custom themes & fonts', included: true },
      { id: 'f-pro-7', text: 'Priority support', included: false },
    ],
  },
  {
    id: 'plan-business',
    name: 'Business',
    price: { monthly: 999, annual: 749 },
    description: 'For brands and teams',
    cta: 'Contact Sales',
    ctaStyle: 'border border-primary text-primary hover:bg-primary/5',
    popular: false,
    features: [
      { id: 'f-biz-1', text: '5 Bio Pages', included: true },
      { id: 'f-biz-2', text: 'Unlimited links', included: true },
      { id: 'f-biz-3', text: 'All templates + custom', included: true },
      { id: 'f-biz-4', text: 'Analytics (1 year)', included: true },
      { id: 'f-biz-5', text: 'Remove BioLink branding', included: true },
      { id: 'f-biz-6', text: 'Custom domain support', included: true },
      { id: 'f-biz-7', text: 'Dedicated support', included: true },
    ],
  },
];

export default function LandingPricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-20 bg-card/50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-700 text-primary uppercase tracking-widest mb-3">Pricing</span>
          <h2 className="text-4xl font-800 text-foreground mb-4">Simple, transparent pricing</h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
            Start free, upgrade when you need more. No hidden fees.
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center gap-3 bg-muted rounded-full p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-600 transition-all ${
                !annual ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-600 transition-all flex items-center gap-2 ${
                annual ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground'
              }`}
            >
              Annual
              <span className="text-xs px-2 py-0.5 rounded-full bg-success/10 text-success font-700">-25%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative bg-card rounded-2xl p-8 border transition-all duration-300 ${
                plan?.popular
                  ? 'border-primary card-shadow-lg scale-105'
                  : 'border-border card-shadow hover:card-shadow-hover hover:-translate-y-1'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-brand text-white text-xs font-700 px-4 py-1.5 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <h3 className="text-xl font-700 text-foreground mb-1">{plan?.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{plan?.description}</p>

              <div className="mb-6">
                {plan?.price?.monthly === 0 ? (
                  <p className="text-4xl font-800 text-foreground font-tabular">Free</p>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-600 text-muted-foreground">₹</span>
                    <span className="text-4xl font-800 text-foreground font-tabular">
                      {annual ? plan?.price?.annual : plan?.price?.monthly}
                    </span>
                    <span className="text-sm text-muted-foreground">/month</span>
                  </div>
                )}
                {annual && plan?.price?.monthly > 0 && (
                  <p className="text-xs text-success font-600 mt-1">
                    Save ₹{(plan?.price?.monthly - plan?.price?.annual) * 12}/year
                  </p>
                )}
              </div>

              <Link
                href="/sign-up-login-screen"
                className={`block text-center text-sm font-600 py-3 rounded-xl transition-all duration-200 mb-6 ${plan?.ctaStyle}`}
              >
                {plan?.cta}
              </Link>

              <ul className="space-y-3">
                {plan?.features?.map((feature) => (
                  <li key={feature?.id} className="flex items-center gap-3 text-sm">
                    {feature?.included ? (
                      <svg className="w-4 h-4 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={feature?.included ? 'text-foreground' : 'text-muted-foreground/60'}>
                      {feature?.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}